import React, { useEffect, useRef } from "react";

const DOT_COUNT = 520;
const ACCENT = "#ff4a1c";

const HeroDotSphere = ({ className = "" }) => {
  const canvasRef = useRef(null);
  const stateRef = useRef({
    dots: [],
    mx: 0,
    my: 0,
    hovering: false,
    raf: 0,
    dpr: 1,
    w: 0,
    h: 0,
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const s = stateRef.current;

    s.dots = Array.from({ length: DOT_COUNT }, () => {
      // Uniform random direction on unit sphere (Marsaglia)
      let x, y, s2;
      do {
        x = Math.random() * 2 - 1;
        y = Math.random() * 2 - 1;
        s2 = x * x + y * y;
      } while (s2 >= 1 || s2 === 0);
      const f = 2 * Math.sqrt(1 - s2);
      const nx = x * f;
      const ny = y * f;
      const nz = 1 - 2 * s2;
      // Radial jitter for an organic, imperfect sphere
      const rad = 0.88 + Math.random() * 0.2;
      return {
        x: nx * rad,
        y: ny * rad,
        z: nz * rad,
        phase: Math.random() * Math.PI * 2,
        freq: 0.8 + Math.random() * 1.8,
        jx: (Math.random() - 0.5) * 0.06,
        jy: (Math.random() - 0.5) * 0.06,
        jz: (Math.random() - 0.5) * 0.06,
        ox: 0,
        oy: 0,
      };
    });

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      s.dpr = Math.min(window.devicePixelRatio || 1, 2);
      s.w = rect.width;
      s.h = rect.height;
      canvas.width = rect.width * s.dpr;
      canvas.height = rect.height * s.dpr;
      ctx.setTransform(s.dpr, 0, 0, s.dpr, 0, 0);
    };
    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      s.mx = e.clientX - rect.left;
      s.my = e.clientY - rect.top;
    };
    const onEnter = () => { s.hovering = true; };
    const onLeave = () => { s.hovering = false; };

    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerenter", onEnter);
    canvas.addEventListener("pointerleave", onLeave);

    const loop = (t) => {
      const { w, h, dots } = s;
      ctx.clearRect(0, 0, w, h);

      const cx = w / 2;
      const cy = h / 2;
      const radius = Math.min(w, h) * 0.42;

      const hoverR = Math.min(w, h) * 0.28;
      const hoverR2 = hoverR * hoverR;
      const pushStrength = Math.min(w, h) * 0.18;

      for (let i = 0; i < dots.length; i++) {
        const p = dots[i];

        const breath = 1 + Math.sin(t * 0.0024 * p.freq + p.phase) * 0.07;
        const wobbleX = Math.sin(t * 0.0022 + p.phase) * p.jx;
        const wobbleY = Math.cos(t * 0.0022 + p.phase * 1.3) * p.jy;
        const wobbleZ = Math.sin(t * 0.0020 + p.phase * 0.7) * p.jz;

        const baseX = cx + (p.x + wobbleX) * radius * breath;
        const baseY = cy + (p.y + wobbleY) * radius * breath;
        const liveZ = p.z + wobbleZ;

        let tx = 0, ty = 0;
        if (s.hovering) {
          const dx = baseX - s.mx;
          const dy = baseY - s.my;
          const d2 = dx * dx + dy * dy;
          if (d2 < hoverR2 && d2 > 0.01) {
            const d = Math.sqrt(d2);
            const f = (1 - d / hoverR);
            const fs = f * f * pushStrength;
            tx = (dx / d) * fs;
            ty = (dy / d) * fs;
          }
        }
        p.ox += (tx - p.ox) * 0.12;
        p.oy += (ty - p.oy) * 0.12;

        const px = baseX + p.ox;
        const py = baseY + p.oy;

        const depth = (liveZ + 1) * 0.5;
        const size = 0.7 + depth * 1.9;
        const alpha = 0.18 + depth * 0.72;

        ctx.globalAlpha = alpha;
        ctx.fillStyle = ACCENT;
        ctx.beginPath();
        ctx.arc(px, py, size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      s.raf = requestAnimationFrame(loop);
    };
    s.raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(s.raf);
      ro.disconnect();
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerenter", onEnter);
      canvas.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={`block w-full h-full ${className}`}
      aria-hidden="true"
    />
  );
};

export default HeroDotSphere;
